import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Send,
  Eye,
  Save,
  GraduationCap,
  PlusCircle,
  ClipboardList,
  RotateCcw,
  History,
  Trash2,
  MessageSquarePlus,
  Database,
  Sparkles,
} from 'lucide-react';
import { ThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import {
  generateCourse,
  generateCourseFromAttachment,
  uploadCourseAttachment,
  addCourse,
  fetchSavedCourse,
  maxAssessments,
  buildAssessments,
  createAssessment,
  generateQuestions,
  generateAutomaticQuestionsInMemory,
  automaticQuestionPlan,
  automaticAssessmentLevel,
  SELECTABLE_QUESTION_TYPES,
  saveQuestions,
  fetchPackageUsage,
  askYakiAgent,
  findCoursesByName,
  listCourses,
  saveChat,
  listChats,
  deleteChat,
} from './courseService';
import { fetchQuestionMix } from '../Instructor/services/questionSelectionApi';
import Questionsdialog from '../Instructor/assignment/questiondialog';
import AutoPreviewDialog from './AutoPreviewDialog';
import MemoryPanel from './MemoryPanel';
import createInstructorMuiTheme from '../Instructor/ui/muiTheme';
import { buildTheme } from '../Instructor/theme';
import yakiImage from '../../main.png';
import './ChatWidget.css';

// Reuse the Instructor design system for the shared QuestionDialog so it looks
// correct even though the chat widget lives outside the Instructor route tree.

/** Read portal theme from the same localStorage keys as `useTweaks`. */
const readInstructorTheme = () => {
  try {
    const raw = localStorage.getItem('instructorTweaks');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.theme === 'dark' || parsed.theme === 'light') return parsed.theme;
    }
  } catch (_) {
    /* ignore corrupt JSON */
  }
  const legacy = localStorage.getItem('instructorTheme');
  return legacy === 'dark' ? 'dark' : 'light';
};

// Yaki mascot — the same image shown on the Login screen. Imported (not read from
// /public) so the bundler emits a content-hashed filename that cache-busts the
// image on every deploy; otherwise a stale CDN copy keeps showing an old logo.
const LOGO_SRC = yakiImage;

/* ------------------------------------------------------------------
   Lightweight, rule-based assistant + a guided "Create a course" flow
   that prompts an LLM for a course outline and saves it through the
   project's add-course API. Self-contained so it can sit on every page.
------------------------------------------------------------------- */
const QUICK_REPLIES = [
  '🎓 Create a course',
  'Create Assessment',
  'What is StudyAK?',
];

// Top-level learning levels; "K-12" expands to grades 1–12.
const LEVEL_OPTIONS = ['K-12', 'University', 'Professional Certification'];
const K12_GRADES = [
  'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12',
];
// Question types (multi-select) — the same four categories the automatic flow plans
// with, so a chosen type really drives generation: Passage generates a passage,
// Image generates one image per question, and every question is tagged with its type.
const QUESTION_TYPES = SELECTABLE_QUESTION_TYPES;
// Questions per assessment: the user may type any amount up to this max.
const MAX_QUESTIONS = 15;
const QUESTION_OPTIONS = [5, 10, 15];

// Fully-automatic defaults (no user input beyond course name + grade).
// Production configuration: exactly 10 categorized questions per assessment
// (technical: 5 Direct + 5 Scenario; otherwise 3 Direct + 3 Scenario + 2 Image
// + 2 Passage, sharing a single image and a single passage). See
// automaticQuestionPlan in courseService.js.
// Bloom's tagging for the automatic flow lives with the rest of the planning logic
// in courseService.js (automaticAssessmentLevel).

// Bloom's-taxonomy assessment levels offered when generating questions.
const assessmentLevels = [
  { label: 'Remember', tooltip: 'Recognizing, Recalling' },
  { label: 'Understand', tooltip: 'Interpreting, Exemplifying, Classifying, Inferring, Comparing, Explaining' },
  { label: 'Apply', tooltip: 'Executing, Implementing' },
  { label: 'Analyze', tooltip: 'Differentiating, Organizing, Attributing' },
  { label: 'Evaluate', tooltip: 'Checking, Critiquing' },
];

const CHAT_STORAGE_KEY = 'studyak_chat_messages';
const CHAT_ID_KEY = 'studyak_chat_id';
// The userId that owns the persisted chat — used to discard a conversation left
// behind by a different account when profiles are switched on the same browser.
const CHAT_OWNER_KEY = 'studyak_chat_owner';
const INITIAL_MESSAGES = [
  {
    from: 'bot',
    text: "Hi! I'm Yaki, your AI Assistant. Ask me anything, or pick a topic below.",
  },
];

const currentUserId = () => localStorage.getItem('userId') || '';

const loadSavedMessages = () => {
  try {
    // Only restore the saved chat if it belongs to the signed-in user; a chat
    // from a previous/other profile must not carry over.
    const owner = localStorage.getItem(CHAT_OWNER_KEY) || '';
    if (owner !== currentUserId()) return INITIAL_MESSAGES;
    const saved = localStorage.getItem(CHAT_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : null;
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : INITIAL_MESSAGES;
  } catch (_) {
    return INITIAL_MESSAGES;
  }
};

const getBotReply = (text) => {
  const q = text.toLowerCase();

  if (/(^|\b)(hi|hello|hey|hii|yo)\b/.test(q)) {
    return "Hi there! 👋 I'm Yaki, your StudyAK assistant. How can I help you today?";
  }
  if (q.includes('what is studyak') || q.includes('about')) {
    return 'StudyAK.ai is an AI-powered learning platform that helps you create, manage and study courses — from grade school to professional certifications.';
  }
  if (q.includes('create assessment')) {
    // The "Create Assessment" flow handles this directly; this is only a fallback
    // for phrasings that don't match the intent.
    return 'Say "Create assessment" and I\'ll show your courses so you can pick one to add assessments to.';
  }
  if (q.includes('sign up') || q.includes('register') || q.includes('account')) {
    return "You can create an account from the Login page — just click \"Sign up\" and follow the steps.";
  }
  if (q.includes('price') || q.includes('pricing') || q.includes('package') || q.includes('plan') || q.includes('cost')) {
    return 'We offer flexible packages for students, instructors and institutions. Check the Packages section on the homepage for current plans and pricing.';
  }
  if (q.includes('support') || q.includes('contact') || q.includes('help') || q.includes('email')) {
    return "Our team is happy to help! You can reach us at info@studyak.com and we'll get back to you shortly.";
  }
  if (q.includes('thank')) {
    return "You're welcome! 😊 Is there anything else I can help you with?";
  }
  // No canned answer — let the caller fall through to the Yaki agent.
  return null;
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  // Falls back to a lucide icon if the logo asset fails to load (e.g. a bad
  // asset path after deployment) so the launcher icon is never blank.
  const [logoOk, setLogoOk] = useState(true);
  // Server-side chat history (stored per userId in yaki_memory).
  const [chatId, setChatId] = useState(() => localStorage.getItem(CHAT_ID_KEY) || `chat-${Date.now()}`);
  const [chatHistory, setChatHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [messages, setMessages] = useState(loadSavedMessages);
  /*
  const [messages, setMessages] = useState([
    {
      from: 'bot',
      text: "Hi! I'm Yaki, the StudyAK assistant. 👋 Ask me anything, or pick a topic below.",
    },
  ]);
  */

  // ---- course / assessment flow state ----
  // flowStep: null | 'askName' | 'askGrade' | 'generating' | 'preview' | 'submitting'
  //           | 'askAssessmentCount' | 'assessmentPreview' | 'savingAssessments'
  //           | 'askQuestionCount' | 'askAssessmentLevel' | 'generatingQuestions'
  //           | 'questionPreview' | 'savingQuestions'
  const [flowStep, setFlowStep] = useState(null);
  const [courseName, setCourseName] = useState('');
  const [grade, setGrade] = useState('');
  // Optional attachment + notes captured after the course name, used to generate
  // the course from a PDF/image and/or extra text (see generateCourseFromAttachment).
  const [attachFile, setAttachFile] = useState(null);
  const [attachText, setAttachText] = useState('');
  const [course, setCourse] = useState(null);
  const [savedCourse, setSavedCourse] = useState(null);
  // Build mode: 'auto' (name+grade → everything → preview) or 'semi' (step by step).
  const [buildMode, setBuildMode] = useState(null);
  const [autoCourse, setAutoCourse] = useState(null);       // generated course (in memory)
  const [autoAssessments, setAutoAssessments] = useState([]); // [{...assessment, questions}]
  const [autoPreviewOpen, setAutoPreviewOpen] = useState(false); // full draft preview dialog
  // Course the Yaki agent acts on for quiz/banks/adaptive/drafts commands.
  const [activeCourseId, setActiveCourseId] = useState(localStorage.getItem('selectedCourseId') || '');
  const [courseCandidates, setCourseCandidates] = useState([]); // duplicate-name picker
  // "Create Assessment" course picker: the instructor's own courses, full records
  // (with domains) so a pick can go straight into the step-by-step flow.
  const [assessmentCourses, setAssessmentCourses] = useState([]);
  const [agentBusy, setAgentBusy] = useState(false);
  const [assessments, setAssessments] = useState([]);

  // ---- per-assessment question flow ----
  const [savedAssessments, setSavedAssessments] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  // how many questions, plus the (multi) question types and Bloom's levels chosen
  const [questionCount, setQuestionCount] = useState(MAX_QUESTIONS);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [showUpgrade, setShowUpgrade] = useState(false);  // show "Upgrade plan" CTA after a limit is hit
  const navigate = useNavigate();
  // Inline "working…" indicator shown in the body whenever a step is awaiting
  // a network call (package checks, saving, generating). Empty string = idle.
  const [loadingLabel, setLoadingLabel] = useState('');
  // Coarse progress for the long automatic build: { done, total }. null hides the bar.
  const [progress, setProgress] = useState(null);

  const bodyRef = useRef(null);
  const typingTimer = useRef(null);
  const skipNextPersist = useRef(false);
  // Tracks which signed-in user the on-screen conversation belongs to, so a
  // profile switch (logout/login, impersonation) resets the chat.
  const activeUserRef = useRef(currentUserId());
  const [portalTheme, setPortalTheme] = useState(readInstructorTheme);
  const scTheme = React.useMemo(() => buildTheme({ theme: portalTheme }), [portalTheme]);
  const muiTheme = React.useMemo(() => createInstructorMuiTheme(scTheme), [scTheme]);
  // Package usage is fetched ONCE per session and validated locally at each
  // step (decremented as the user creates courses/assessments) — see
  // loadUsageOnce / decCourse / decAssessments below. `undefined` = not loaded.
  const usageRef = useRef(undefined);

  // Hard re-entrancy lock for the two "save assessments" handlers. State/flowStep
  // guards depend on a re-render landing before the next click; this ref flips
  // synchronously at the top of the handler, so a second trigger (double-click,
  // render race, StrictMode) can never run the create loop a second time and
  // duplicate every assessment.
  const savingRef = useRef(false);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, isTyping, loadingLabel, isOpen, course, assessments, questions, flowStep]);

  useEffect(() => {
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
    localStorage.setItem(CHAT_OWNER_KEY, currentUserId());
  }, [messages]);

  // Keep the active chatId in localStorage so reloads reuse the same history
  // entry instead of creating a duplicate each time.
  useEffect(() => {
    localStorage.setItem(CHAT_ID_KEY, chatId);
  }, [chatId]);

  // Load the user's saved chats from the server whenever the widget opens.
  useEffect(() => {
    const uid = localStorage.getItem('userId');
    if (isOpen && uid) {
      listChats(uid).then(setChatHistory).catch(() => {});
    }
  }, [isOpen]);

  // Auto-save the current chat (debounced) once it contains a real exchange.
  useEffect(() => {
    const uid = localStorage.getItem('userId');
    if (!uid) return undefined;
    const firstUser = messages.find((m) => m.from === 'user');
    if (!firstUser) return undefined; // skip greeting-only chats
    const title = (firstUser.text || 'Chat').slice(0, 48);
    const t = setTimeout(() => {
      saveChat({ userId: uid, chatId, title, messages });
    }, 1500);
    return () => clearTimeout(t);
  }, [messages, chatId]);

  useEffect(() => {
    const clearSavedChat = () => {
      skipNextPersist.current = true;
      localStorage.removeItem(CHAT_STORAGE_KEY);
      setMessages(INITIAL_MESSAGES);
    };

    window.addEventListener('studyak_chat_clear', clearSavedChat);
    return () => window.removeEventListener('studyak_chat_clear', clearSavedChat);
  }, []);

  // Reset the conversation whenever the signed-in user changes while the widget
  // stays mounted (profile switch / impersonation / re-login). Without this the
  // previous account's messages would linger for the next user.
  useEffect(() => {
    const syncUser = () => {
      const uid = currentUserId();
      if (uid === activeUserRef.current) return;
      activeUserRef.current = uid;
      skipNextPersist.current = true;
      localStorage.removeItem(CHAT_STORAGE_KEY);
      localStorage.removeItem(CHAT_OWNER_KEY);
      localStorage.removeItem(CHAT_ID_KEY);
      setChatId(`chat-${Date.now()}`);
      setMessages(INITIAL_MESSAGES);
      setFlowStep(null);
      setHistoryOpen(false);
      setChatHistory([]);
      if (uid) listChats(uid).then(setChatHistory).catch(() => {});
    };
    window.addEventListener('storage', syncUser);
    window.addEventListener('focus', syncUser);
    const intervalId = window.setInterval(syncUser, 1000);
    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener('focus', syncUser);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  // Stay in sync with the instructor portal theme toggle (widget is outside ThemeProvider).
  useEffect(() => {
    const syncTheme = () => {
      const next = readInstructorTheme();
      setPortalTheme((prev) => (prev === next ? prev : next));
    };
    syncTheme();
    window.addEventListener('storage', syncTheme);
    window.addEventListener('focus', syncTheme);
    const intervalId = isOpen ? window.setInterval(syncTheme, 400) : null;
    return () => {
      window.removeEventListener('storage', syncTheme);
      window.removeEventListener('focus', syncTheme);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [isOpen]);

  const pushBot = (text, extra) => setMessages((prev) => [...prev, { from: 'bot', text, ...(extra || {}) }]);
  const pushUser = (text) => setMessages((prev) => [...prev, { from: 'user', text }]);
  const packageExceededMessage = (type) =>
    `You have exceeded your package ${type} limit. Please upgrade your package.`;
  // Same message, but also reveals the "Upgrade plan" call-to-action in the chat.
  const limitMsg = (type) => { setShowUpgrade(true); return packageExceededMessage(type); };

  const getPackageUsageSafely = async () => {
    try {
      return await fetchPackageUsage();
    } catch (err) {
      console.warn('[StudyAK chat] package usage check failed:', err);
      return null;
    }
  };

  // Fetch the package usage a single time, then reuse it for every later check.
  const loadUsageOnce = async () => {
    if (usageRef.current !== undefined) return usageRef.current;
    usageRef.current = await getPackageUsageSafely();
    return usageRef.current;
  };

  // Local validators that read the already-loaded usage (no network).
  const courseQuotaLeft = () => {
    const u = usageRef.current;
    return !u || u.courseRemaining === Infinity || u.courseRemaining > 0;
  };
  const assessmentQuotaLeft = (need = 1) => {
    const u = usageRef.current;
    return !u || u.assessmentRemaining === Infinity || u.assessmentRemaining >= need;
  };
  const assessmentRemaining = () => {
    const u = usageRef.current;
    return u ? u.assessmentRemaining : Infinity;
  };
  const decCourse = () => {
    const u = usageRef.current;
    if (u && u.courseRemaining !== Infinity) {
      u.courseRemaining = Math.max(u.courseRemaining - 1, 0);
    }
  };
  const decAssessments = (k) => {
    const u = usageRef.current;
    if (u && u.assessmentRemaining !== Infinity) {
      u.assessmentRemaining = Math.max(u.assessmentRemaining - k, 0);
    }
  };

  // a bot reply that shows the typing indicator first
  const botReplyDelayed = (text, delay = 600) => {
    setIsTyping(true);
    typingTimer.current = setTimeout(() => {
      pushBot(text);
      setIsTyping(false);
    }, delay);
  };

  /* ---------------- course flow ---------------- */
  const startCourseFlow = async () => {
    pushUser('🎓 Create a course');
    setShowUpgrade(false);
    setLoadingLabel('Checking your package…');
    await loadUsageOnce();
    setLoadingLabel('');
    if (!courseQuotaLeft()) {
      setFlowStep(null);
      botReplyDelayed(limitMsg('course'));
      return;
    }
    setCourse(null);
    setCourseName('');
    setGrade('');
    setAttachFile(null);
    setAttachText('');
    setBuildMode(null);
    setAutoCourse(null);
    setAutoAssessments([]);
    setFlowStep('chooseMode');
    botReplyDelayed(
      "Great — how would you like to build it? Pick ⚡ Fully automatic (just give me the course name + grade and I'll create the course, assessments and questions for you to review) or ✍️ Step by step (you choose the number of assessments and questions) below.",
    );
  };

  /**
   * "Create Assessment" — add assessments to a course that already exists.
   * Lists the instructor's courses; picking one hands straight over to the same
   * step-by-step flow used after a new course is saved.
   */
  const startAssessmentFlow = async () => {
    pushUser('🧪 Create Assessment');
    setShowUpgrade(false);
    setLoadingLabel('Loading your courses…');
    try {
      await loadUsageOnce();
      if (!assessmentQuotaLeft(1)) {
        setLoadingLabel('');
        setFlowStep(null);
        botReplyDelayed(limitMsg('assessment'));
        return;
      }
      const list = await listCourses();
      setLoadingLabel('');
      if (!list.length) {
        setFlowStep(null);
        botReplyDelayed(
          "You don't have any courses yet. Say \"Create a course\" and I'll build one with you first.",
        );
        return;
      }
      setAssessmentCourses(list);
      setFlowStep('selectAssessmentCourse');
      botReplyDelayed(
        `Which course should the assessment belong to? Pick one below (or type its number).`,
        300,
      );
    } catch (err) {
      setLoadingLabel('');
      setFlowStep(null);
      if (err.code === 'NOT_LOGGED_IN') {
        pushBot('You need to be logged in as an instructor to create an assessment. Please log in and try again.');
      } else {
        console.error(err);
        pushBot("Sorry — I couldn't load your courses. Please try again.");
      }
    }
  };

  /** Course chosen for a new assessment → hand over to the step-by-step flow. */
  const chooseAssessmentCourse = (c) => {
    const domains = c.domains || [];
    pushUser(c.courseName);
    setAssessmentCourses([]);
    setActiveCourseId(c.courseId);

    if (!domains.length) {
      setFlowStep(null);
      botReplyDelayed(
        `"${c.courseName}" has no domains yet, so I can't split assessments across it. Add domains to the course first, then try again.`,
      );
      return;
    }

    // Same state the flow reaches after saving a brand-new course, so everything
    // downstream (question count, types, Bloom level, generation) is unchanged.
    setBuildMode('semi');
    setSavedCourse({ ...c, domains });
    setSavedAssessments([]);
    setCurrentIdx(0);
    setQuestions([]);
    setFlowStep('askAssessmentCount');
    botReplyDelayed(
      `How many assessments would you like for "${c.courseName}"? (Pick below, type a number, or say "No thanks".)`,
      300,
    );
  };

  // Pick the build mode, then collect the (shared) course name + grade.
  const chooseMode = (mode) => {
    setBuildMode(mode);
    pushUser(mode === 'auto' ? '⚡ Fully automatic' : '✍️ Step by step');
    setCourse(null);
    setCourseName('');
    setGrade('');
    setAttachFile(null);
    setAttachText('');
    setAutoCourse(null);
    setAutoAssessments([]);
    setFlowStep('askName');
    botReplyDelayed(
      mode === 'auto'
        ? "Fully automatic it is! 📚 What's the course name? (e.g. \"Algebra\" or \"Biology\")"
        : "Great — let's build a course! 📚 What's the course name? (e.g. \"Algebra\" or \"Biology\")",
    );
  };

  const submitCourseName = (value) => {
    const name = value.trim();
    if (!name) return;
    pushUser(name);
    setCourseName(name);
    setInput('');
    setAttachFile(null);
    setAttachText('');
    setFlowStep('askAttachment');
    botReplyDelayed(
      `Got it — "${name}". 👍 Want to base this course on a document? Attach a PDF or image below, type any notes in the box, then tap Continue — or Skip.`,
    );
  };

  /* ---------------- attachment step (after course name) ---------------- */
  // Capture a selected PDF/image; the actual generation happens after the grade
  // is chosen (see generateCourseForFlow), reusing the attachment + notes.
  const handleAttachSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setAttachFile(file);
    e.target.value = ''; // let the same file be re-selected after removal
  };
  const removeAttachFile = () => setAttachFile(null);

  // Proceed from the attach step to the grade question, keeping file/notes.
  const continueFromAttachment = () => {
    const bits = [];
    if (attachFile) bits.push(`📎 ${attachFile.name}`);
    if (attachText.trim()) bits.push(`📝 ${attachText.trim()}`);
    pushUser(bits.length ? bits.join(' · ') : 'Continue');
    setFlowStep('askGrade');
    botReplyDelayed('👍 Which grade or level is this course for?');
  };
  // Skip attachments entirely — clears any file/notes, then asks for the grade.
  const skipAttachment = () => {
    setAttachFile(null);
    setAttachText('');
    pushUser('Skip');
    setFlowStep('askGrade');
    botReplyDelayed('No problem. Which grade or level is this course for?');
  };

  // True when the user supplied an attachment and/or notes to base the course on.
  const hasAttachmentInput = () => !!attachFile || !!attachText.trim();

  // Step 1: pick the top-level level. K-12 drills into grades 1–12;
  // University / Professional Certification go straight to generation.
  const submitGrade = (value) => {
    const g = value.trim();
    if (!g) return;
    pushUser(g);
    setInput('');
    const cat = LEVEL_OPTIONS.find((o) => o.toLowerCase() === g.toLowerCase());
    if (cat === 'K-12') {
      setFlowStep('askGradeLevel');
      botReplyDelayed('Which grade is this course for? Pick 1–12 below.', 300);
      return;
    }
    proceedAfterGrade(g);
  };

  // After name + grade: fully-auto generates everything; semi-auto shows the outline.
  const proceedAfterGrade = (g) => {
    if (buildMode === 'auto') {
      runFullyAuto(g);
    } else {
      generateCourseOutline(g);
    }
  };

  // Step 2 (K-12 only): pick the specific grade 1–12.
  const submitGradeLevel = (value) => {
    const g = value.trim();
    if (!g) return;
    pushUser(g);
    setInput('');
    proceedAfterGrade(g);
  };

  // Picks attachment-based generation when the user provided a PDF/image or notes,
  // otherwise the standard outline generation. Returns the same normalised course.
  const generateCourseForFlow = (g) =>
    hasAttachmentInput()
      ? generateCourseFromAttachment({ courseName, grade: g, file: attachFile, text: attachText })
      : generateCourse(courseName, g);

  const generateCourseOutline = async (g) => {
    setGrade(g);
    setFlowStep('generating');
    setLoadingLabel(hasAttachmentInput() ? 'Reading your document and generating the course…' : 'Generating course outline…');
    try {
      const generated = await generateCourseForFlow(g);
      setCourse(generated);
      setLoadingLabel('');
      setFlowStep('preview');
      pushBot(`Here's a suggested outline for "${courseName}" (${g}). Review it below and tap "Add course" to save it.`);
    } catch (err) {
      setLoadingLabel('');
      setFlowStep(null);
      pushBot('Sorry, I had trouble generating that course. Please try again.');
    }
  };

  /* ---------------- fully-automatic flow ---------------- */
  // Generates course + assessments + questions entirely in memory, then lets
  // the user preview and Save all or Discard (nothing is persisted until Save).
  const runFullyAuto = async (g) => {
    setGrade(g);
    setFlowStep('autoGenerating');
    setLoadingLabel(hasAttachmentInput() ? 'Reading your document and generating your course…' : 'Generating your course…');
    try {
      const generated = await generateCourseForFlow(g);
      const domainCount = (generated.domains || []).length;
      // Editable Non-Technical mix (System → Question type selection). Fetched once
      // and reused for every assessment; null falls back to the built-in default.
      const questionMix = await fetchQuestionMix();
      setLoadingLabel('Designing assessments…');
      // One assessment per domain. buildAssessments preserves the complete
      // subdomain list when the requested count equals the domain count.
      const built = buildAssessments(generated, domainCount);
      // Name them as distinct exam sets (per integration plan: "Exam Set N",
      // kept separate from "Practice Set N").
      built.forEach((a, i) => { a.name = `${generated.courseName} - Exam Set ${i + 1}`; });

      // Plus ONE course-wide assessment covering every domain, so N domains yield
      // N+1 assessments. Asking buildAssessments for a single assessment gives it
      // the complete domain list; its exam code is suffixed so it can't collide
      // with the per-domain codes (which run -A1..-AN).
      const [courseWide] = buildAssessments(generated, 1);
      if (courseWide) {
        courseWide.name = `${generated.courseName} - Pre Evaluation`;
        courseWide.examCode = `${courseWide.examCode}-PE`;
        built.push(courseWide);
      }

      // Assessments are independent, so they build concurrently instead of one
      // after another — with the single-response question call this is what keeps
      // the whole course down to roughly the time of one assessment.
      let done = 0;
      setProgress({ done: 0, total: built.length });
      setLoadingLabel(`Generating questions for ${built.length} assessment${built.length > 1 ? 's' : ''}…`);
      const withQuestions = await Promise.all(built.map(async (assessment, i) => {
        // Exactly one Bloom's level per assessment, recorded on the assessment so the
        // preview shows the same tag the questions were generated for.
        const level = automaticAssessmentLevel(i);
        assessment.assessmentLevel = level;
        let qs = [];
        try {
          qs = await generateAutomaticQuestionsInMemory(
            assessment, generated, g, [level], () => {}, questionMix,
          );
        } catch (assessmentError) {
          // One failed assessment must not discard the whole draft.
          console.warn(
            `[StudyAK chat] Questions failed for "${assessment.name}"; continuing with the rest.`,
            assessmentError,
          );
        }
        done += 1;
        setProgress({ done, total: built.length });
        const valid = (qs || []).filter((q) => q && Array.isArray(q.answer) && q.answer.length > 0);
        return { ...assessment, questions: valid };
      }));

      setProgress(null);
      setLoadingLabel('');
      setAutoCourse(generated);
      setAutoAssessments(withQuestions);
      setFlowStep('autoPreview');
      const allQuestions = withQuestions.flatMap((a) => a.questions);
      const totalQ = allQuestions.length;

      // Per-type breakdown so the draft summary reflects exactly what generated —
      // and flags any planned category (or the shared image) that didn't come
      // through, instead of it silently going missing from the preview.
      const typeCounts = allQuestions.reduce((counts, q) => {
        const type = q.questionType || 'Direct';
        counts[type] = (counts[type] || 0) + 1;
        return counts;
      }, {});
      const plannedTypes = automaticQuestionPlan(generated, questionMix).map((p) => p.type);
      const breakdown = plannedTypes
        .filter((type) => typeCounts[type])
        .map((type) => `${typeCounts[type]} ${type}`)
        .join(', ');
      const missingTypes = plannedTypes.filter((type) => !typeCounts[type]);
      const imageQuestions = allQuestions.filter((q) => q.questionType === 'Image');
      const imageMissingVisual = imageQuestions.length > 0 && imageQuestions.every((q) => !q.asset);

      pushBot(
        `Here's a full draft for "${generated.courseName}" (${g}): ${withQuestions.length} assessment${withQuestions.length === 1 ? '' : 's'} with ${totalQ} question${totalQ === 1 ? '' : 's'} in total${breakdown ? ` (${breakdown})` : ''}. Open Preview to see the shared image and passage, then Save all or Discard.`,
      );
      if (missingTypes.length) {
        pushBot(
          `Heads up: I couldn't generate ${missingTypes.join(' or ')} question${missingTypes.length === 1 ? '' : 's'} this time. You can Save all with the rest, or Discard and try again.`,
        );
      }
      if (imageMissingVisual) {
        pushBot(
          "The image question's picture couldn't be generated this time, so it'll save without a visual. Discard and try again if you'd like the image.",
        );
      }
    } catch (err) {
      console.error(err);
      setProgress(null);
      setLoadingLabel('');
      setFlowStep(null);
      pushBot('Sorry — automatic generation failed. Please try again.');
    }
  };

  const handleAutoSaveAll = async () => {
    if (savingRef.current) return; // a save is already in flight — ignore re-triggers
    if (!autoCourse) return;
    // Quota check (course + the assessments we're about to create).
    if (!courseQuotaLeft()) { pushBot(limitMsg('course')); return; }
    savingRef.current = true;
    setFlowStep('autoSaving');
    setLoadingLabel('Saving course…');
    let courseSaved = false;
    try {
      await addCourse(autoCourse);
      courseSaved = true;
      decCourse();
      const saved = await fetchSavedCourse(autoCourse.courseName);
      const courseId = saved && saved.courseId ? saved.courseId : autoCourse.courseId;

      // Persist the source attachment to blob storage against the new course
      // (best-effort — never block course creation if this fails).
      if (attachFile && courseId) {
        setLoadingLabel('Saving your document…');
        try {
          await uploadCourseAttachment(courseId, attachFile);
        } catch (uploadErr) {
          console.warn('[StudyAK chat] attachment save failed:', uploadErr);
        }
      }

      let savedAssessmentCount = 0;
      let savedQuestionCount = 0;
      for (let i = 0; i < autoAssessments.length; i++) {
        setLoadingLabel(`Saving assessment ${i + 1} of ${autoAssessments.length}…`);
        const { questions, ...assessmentPayload } = autoAssessments[i];
        // eslint-disable-next-line no-await-in-loop
        const savedA = await createAssessment({ ...assessmentPayload, courseId });
        savedAssessmentCount += 1;
        if (savedA && savedA.testId && questions.length) {
          // eslint-disable-next-line no-await-in-loop
          await saveQuestions(savedA.testId, questions);
          savedQuestionCount += questions.length;
        }
      }
      decAssessments(savedAssessmentCount);

      setLoadingLabel('');
      setFlowStep(null);
      setAutoCourse(null);
      setAutoAssessments([]);
      setBuildMode(null);
      pushBot(
        `✅ Saved! "${autoCourse.courseName}" with ${savedAssessmentCount} assessment${savedAssessmentCount === 1 ? '' : 's'} and ${savedQuestionCount} question${savedQuestionCount === 1 ? '' : 's'} is ready in your catalog.`,
      );
    } catch (err) {
      console.error(err);
      setLoadingLabel('');
      if (!courseSaved) {
        // Course wasn't saved yet — keep the draft so the user can retry.
        setFlowStep('autoPreview');
        if (err.code === 'NOT_LOGGED_IN') {
          pushBot('You need to be logged in as an instructor to save. Please log in and try again.');
        } else {
          pushBot('Sorry — saving failed. Your draft is still here; tap “Save all” to retry.');
        }
      } else {
        // Course is already saved — don't offer a retry that would duplicate it.
        setFlowStep(null);
        setAutoCourse(null);
        setAutoAssessments([]);
        setBuildMode(null);
        pushBot('Your course was saved, but I hit a problem creating some assessments. Please add the remaining ones from your course list.');
      }
    } finally {
      savingRef.current = false;
    }
  };

  const handleAutoDiscard = () => {
    pushUser('Discard');
    setAutoCourse(null);
    setAutoAssessments([]);
    setBuildMode(null);
    setFlowStep(null);
    botReplyDelayed('No problem — I discarded that draft. Nothing was saved. Anything else?');
  };


  const handleAddCourse = async () => {
    if (!course) return;
    // Validate against the usage we loaded once — no re-fetch here.
    if (!courseQuotaLeft()) {
      setFlowStep('preview');
      pushBot(limitMsg('course'));
      return;
    }
    const savedName = course.courseName;
    setFlowStep('submitting');
    setLoadingLabel('Saving your course…');
    try {
      await addCourse(course);
      decCourse(); // one course used
      pushBot(`✅ "${savedName}" has been added to your course catalog!`);
      setCourse(null);

      // Re-fetch the saved course so we have backend ids for assessments.
      setLoadingLabel('Preparing assessments…');
      let saved = null;
      try {
        saved = await fetchSavedCourse(savedName);
      } catch (_) {
        /* non-fatal — just skip the assessment offer */
      }
      setLoadingLabel('');
      if (saved && saved.courseId) setActiveCourseId(saved.courseId);

      // Persist the source attachment to blob storage against the new course
      // (best-effort — never block the flow if this fails).
      if (attachFile && saved && saved.courseId) {
        try {
          await uploadCourseAttachment(saved.courseId, attachFile);
        } catch (uploadErr) {
          console.warn('[StudyAK chat] attachment save failed:', uploadErr);
        }
      }

      // Prefer the re-fetched course's domains (they carry backend ids); fall
      // back to the just-generated outline's domains if the re-fetch didn't
      // include them, so the assessment offer still appears.
      const domains = saved && (saved.domains || []).length > 0
        ? saved.domains
        : (course && course.domains ? course.domains : []);

      if (saved && domains.length > 0) {
        if (!assessmentQuotaLeft(1)) {
          setSavedCourse(null);
          setFlowStep(null);
          botReplyDelayed(limitMsg('assessment'), 700);
          return;
        }
        setSavedCourse({ ...saved, domains });
        setFlowStep('askAssessmentCount');
        botReplyDelayed(
          `Would you like me to create assessments for "${savedName}"? How many? (You can pick below, type a number, or say "No thanks".)`,
          700
        );
      } else {
        // No backend course to attach assessments to (or no domains) — close
        // gracefully so the chat never stalls after "course added".
        setSavedCourse(null);
        setFlowStep(null);
        botReplyDelayed(
          `Your course "${savedName}" is saved. You can add assessments to it anytime from your course list. Anything else I can help with?`,
          700
        );
      }
    } catch (err) {
      setLoadingLabel('');
      setFlowStep('preview');
      if (err.code === 'NOT_LOGGED_IN') {
        pushBot('You need to be logged in as an instructor to save a course. Please log in and try again.');
      } else {
        pushBot('Sorry, something went wrong while saving the course. Please try again.');
      }
    }
  };

  const cancelCourse = () => {
    setCourse(null);
    setFlowStep(null);
    pushBot('No problem — the course was discarded. Is there anything else I can help with?');
  };

  /* ---------------- assessment flow ---------------- */
  const submitAssessmentCount = async (value) => {
    const raw = value.trim();
    pushUser(raw);
    setInput('');

    if (/^(no|nope|no thanks|skip|cancel|not now)/i.test(raw)) {
      setFlowStep(null);
      setSavedCourse(null);
      botReplyDelayed('No problem! Your course is ready. Is there anything else I can help with?');
      return;
    }

    const n = parseInt(raw, 10);
    if (isNaN(n) || n < 1) {
      botReplyDelayed('Please enter a valid number of assessments — e.g. 1, 2 or 3.');
      return;
    }

    // Validate locally against the once-loaded usage (no re-fetch).
    if (!assessmentQuotaLeft(1)) {
      setFlowStep('askAssessmentCount');
      botReplyDelayed(limitMsg('assessment'));
      return;
    }
    const remaining = assessmentRemaining();
    if (remaining !== Infinity && n > remaining) {
      setFlowStep('askAssessmentCount');
      botReplyDelayed(
        `You have only ${remaining} assessment${remaining === 1 ? '' : 's'} left in your package. Please upgrade your package.`
      );
      return;
    }

    setLoadingLabel('Preparing assessments…');
    const built = buildAssessments(savedCourse, n);
    if (!built.length) {
      setLoadingLabel('');
      setFlowStep('askAssessmentCount');
      botReplyDelayed("I couldn't build assessments from this course's domains. Want to try a different number?");
      return;
    }
    setLoadingLabel('');

    // Introduce the assessments first, then reveal the cards, so the message
    // reads above the preview instead of appearing after it.
    const tail = `Each one groups domains and subdomains evenly. You can adjust which domains each covers and pick a question type below, then tap "Agree & save" to create ${built.length > 1 ? 'them' : 'it'}.`;
    pushBot(
      `Here ${built.length > 1 ? 'are' : 'is'} ${built.length} assessment${built.length > 1 ? 's' : ''} for "${savedCourse.courseName}". ${tail}`
    );
    setAssessments(built);
    setFlowStep('assessmentPreview');
  };

  const handleSaveAssessments = async () => {
    if (savingRef.current) return; // a save is already in flight — ignore re-triggers
    if (!assessments.length) return;
    // Validate locally against the once-loaded usage (no re-fetch).
    if (!assessmentQuotaLeft(1)) {
      setFlowStep('assessmentPreview');
      pushBot(limitMsg('assessment'));
      return;
    }
    const remaining = assessmentRemaining();
    if (remaining !== Infinity && assessments.length > remaining) {
      setFlowStep('assessmentPreview');
      pushBot(
        `You have only ${remaining} assessment${remaining === 1 ? '' : 's'} left in your package. Please upgrade your package.`
      );
      return;
    }
    // Every assessment must cover at least one domain — the preview lets the user
    // deselect domains, so guard against saving an empty (question-less) assessment.
    if (assessments.some((a) => !(a.domains && a.domains.length))) {
      setFlowStep('assessmentPreview');
      pushBot('Each assessment needs at least one domain. Please choose a domain for every assessment before saving.');
      return;
    }
    savingRef.current = true;
    setFlowStep('savingAssessments');
    try {
      const created = [];
      for (let i = 0; i < assessments.length; i++) {
        setLoadingLabel(
          assessments.length > 1
            ? `Saving assessment ${i + 1} of ${assessments.length}…`
            : 'Saving assessment…'
        );
        // questionTypes is a UI-only per-assessment choice from the preview: keep it
        // off the API payload, but carry it onto the saved assessment so the question
        // step can use it instead of asking for the type again.
        const { questionTypes, ...payload } = assessments[i];
        // eslint-disable-next-line no-await-in-loop
        const saved = await createAssessment(payload);
        created.push({ ...saved, questionTypes: questionTypes || [] });
      }
      decAssessments(created.length); // account for the assessments just created
      setLoadingLabel('');
      const n = created.length;
      pushBot(`✅ ${n} assessment${n > 1 ? 's' : ''} created successfully!`);
      setAssessments([]);
      setSavedCourse(null);
      setSavedAssessments(created);
      setCurrentIdx(0);
      setFlowStep('askQuestionCount');
      botReplyDelayed(
        `Now let's add questions for "${created[0].name}". How many would you like? Type a number up to ${MAX_QUESTIONS}, pick one below, or say "Skip".`,
        700
      );
    } catch (err) {
      setLoadingLabel('');
      setFlowStep('assessmentPreview');
      pushBot('Sorry, something went wrong while saving the assessments. Please try again.');
    } finally {
      savingRef.current = false;
    }
  };

  const cancelAssessments = () => {
    setAssessments([]);
    setSavedCourse(null);
    setFlowStep(null);
    pushBot('Okay, I discarded the assessments. Your course is still saved. Anything else?');
  };

  // ---- assessment-preview editing (step-by-step): choose domains + type ----
  const domainKey = (d) => d.domainId || d.domain_id || d.name;

  // Toggle a whole domain (with its subdomains) on one preview assessment,
  // overriding buildAssessments' automatic even split.
  const toggleAssessmentDomain = (ai, domain) => {
    setAssessments((prev) =>
      prev.map((a, i) => {
        if (i !== ai) return a;
        const has = (a.domains || []).some((d) => domainKey(d) === domainKey(domain));
        const domains = has
          ? a.domains.filter((d) => domainKey(d) !== domainKey(domain))
          : [...(a.domains || []), domain];
        return { ...a, domains };
      })
    );
  };

  // Toggle a question type for one preview assessment; used when its questions
  // are generated (see submitQuestionCount).
  const toggleAssessmentType = (ai, t) => {
    setAssessments((prev) =>
      prev.map((a, i) => {
        if (i !== ai) return a;
        const cur = a.questionTypes || [];
        const questionTypes = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
        return { ...a, questionTypes };
      })
    );
  };

  /* ---------------- per-assessment question flow ---------------- */
  // Move on to the next assessment, or finish when all are done.
  const advanceToNextAssessment = (idx) => {
    const next = idx + 1;
    if (next < savedAssessments.length) {
      setCurrentIdx(next);
      setQuestions([]);
      setQuestionDialogOpen(false);
      setFlowStep('askQuestionCount');
      botReplyDelayed(
        `How many questions for "${savedAssessments[next].name}"? Type a number up to ${MAX_QUESTIONS}, pick one below, or say "Skip".`,
        500
      );
    } else {
      setSavedAssessments([]);
      setCurrentIdx(0);
      setQuestionDialogOpen(false);
      setFlowStep(null);
      botReplyDelayed('🎉 All done! Your course, assessments and questions are ready. Anything else?', 400);
    }
  };

  // Step 1: how many questions (any number up to MAX_QUESTIONS).
  const submitQuestionCount = (value) => {
    const raw = value.trim();
    pushUser(raw);
    setInput('');

    if (/^(no|nope|skip|none|not now|cancel)/i.test(raw)) {
      advanceToNextAssessment(currentIdx);
      return;
    }

    const n = parseInt(raw, 10);
    if (isNaN(n) || n < 1) {
      botReplyDelayed(
        `Please enter a valid number of questions between 1 and ${MAX_QUESTIONS}, or say "Skip".`
      );
      return;
    }
    if (n > MAX_QUESTIONS) {
      botReplyDelayed(
        `I can generate up to ${MAX_QUESTIONS} questions at a time. Please choose a number up to ${MAX_QUESTIONS}.`
      );
      return;
    }

    setQuestionCount(n);
    setSelectedLevels([]);
    // If the question type was already chosen for this assessment on the
    // assessment preview, skip the type step and go straight to the Bloom's
    // level. Otherwise fall back to asking for the type here.
    const preTypes = savedAssessments[currentIdx]?.questionTypes || [];
    if (preTypes.length) {
      setSelectedTypes(preTypes);
      setFlowStep('askAssessmentLevel');
      botReplyDelayed(
        `Great — ${n} question${n > 1 ? 's' : ''} · types: ${preTypes.join(', ')}. Which assessment level (Bloom's taxonomy)? Select one below, then tap Continue.`,
        400
      );
    } else {
      setSelectedTypes([]);
      setFlowStep('askQuestionType');
      botReplyDelayed(
        `Great — ${n} question${n > 1 ? 's' : ''}. Which question type(s) would you like? Select one or more below, then tap Continue.`,
        400
      );
    }
  };

  // Step 2: multi-select question type(s).
  const toggleType = (t) =>
    setSelectedTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const continueTypes = () => {
    if (!selectedTypes.length) { botReplyDelayed('Please select at least one question type below.'); return; }
    pushUser(selectedTypes.join(', '));
    setFlowStep('askAssessmentLevel');
    botReplyDelayed("Which assessment level (Bloom's taxonomy)? Select one below, then tap Continue.", 300);
  };

  // Step 3: single-select Bloom's level (only one level may be chosen).
  const toggleLevel = (l) =>
    setSelectedLevels((prev) => (prev[0] === l ? [] : [l]));
  const continueLevels = () => {
    if (!selectedLevels.length) { botReplyDelayed('Please select one assessment level below.'); return; }
    pushUser(selectedLevels.join(', '));
    setFlowStep('askSettings');
    botReplyDelayed(
      `Ready to generate ${questionCount} question${questionCount > 1 ? 's' : ''} · types: ${selectedTypes.join(', ')} · level: ${selectedLevels.join(', ')}. Generate now, or update these settings?`,
      300
    );
  };

  // Step 4: confirm — generate now, or go back and update the settings.
  const updateSettings = () => {
    pushUser('Update settings');
    setSelectedTypes([]);
    setSelectedLevels([]);
    setFlowStep('askQuestionCount');
    botReplyDelayed(
      `Sure — let's update. How many questions for "${savedAssessments[currentIdx] ? savedAssessments[currentIdx].name : 'this assessment'}"? Type a number up to ${MAX_QUESTIONS}, or pick one below.`,
      300
    );
  };

  // Step 5: generate `n` questions at the chosen grade, type(s) and level(s).
  const runGenerateQuestions = async (n, types, levels) => {
    const assessment = savedAssessments[currentIdx];
    if (!assessment) return;
    pushUser('Generate');
    setFlowStep('generatingQuestions');
    setLoadingLabel(`Generating ${n} question${n > 1 ? 's' : ''} for "${assessment.name}"…`);
    try {
      const { testId, questions: generated } = await generateQuestions(
        assessment, n, grade, levels, types
      );
      setLoadingLabel('');
      // keep only well-formed MCQs (the review dialog renders answers unguarded)
      const valid = (generated || []).filter(
        (q) => q && Array.isArray(q.answer) && q.answer.length > 0
      );
      if (!valid.length) {
        setFlowStep('askQuestionCount');
        pushBot("I couldn't generate questions this time. Want to try a different number?");
        return;
      }
      // remember the resolved testId for saving
      setSavedAssessments((prev) =>
        prev.map((a, i) => (i === currentIdx ? { ...a, testId } : a))
      );
      setQuestions(valid);
      setQuestionDialogOpen(false);
      setFlowStep('questionPreview');
      pushBot(
        `Generated ${valid.length} question${valid.length > 1 ? 's' : ''} for "${assessment.name}". Tap "Preview Questions" to review them, or save them now.`
      );
    } catch (err) {
      setLoadingLabel('');
      setFlowStep('askQuestionCount');
      if (err.code === 'NO_TEST_ID') {
        pushBot("I couldn't locate this assessment to attach questions to. Let's skip it for now.");
        advanceToNextAssessment(currentIdx);
      } else {
        pushBot('Sorry, generating questions failed. Want to try again?');
      }
    }
  };

  // Persist the questions (optionally an edited list from the dialog).
  const handleSaveQuestions = async (list) => {
    const toSave = Array.isArray(list) ? list : questions;
    if (!toSave.length) return;
    const assessment = savedAssessments[currentIdx];
    setFlowStep('savingQuestions');
    setLoadingLabel('Saving questions…');
    try {
      await saveQuestions(assessment.testId, toSave);
      setLoadingLabel('');
      setQuestionDialogOpen(false);
      pushBot(`✅ Saved ${toSave.length} question${toSave.length > 1 ? 's' : ''} to "${assessment.name}".`);
      advanceToNextAssessment(currentIdx);
    } catch (err) {
      setLoadingLabel('');
      setFlowStep('questionPreview');
      pushBot('Sorry, something went wrong while saving the questions. Please try again.');
    }
  };

  // "Show questions" -> minimize the chat panel and open the full QuestionDialog.
  const handleShowQuestions = () => {
    setIsOpen(false); // minimize the chat
    setQuestionDialogOpen(true); // open the shared question dialog
  };

  // Save handler used by the shared QuestionDialog (receives the edited list).
  const handleDialogSave = async (editedContent) => {
    await handleSaveQuestions(editedContent);
  };

  // Close/cancel from the dialog: dismiss it and restore the chat panel.
  const handleDialogClose = () => {
    setQuestionDialogOpen(false);
    setIsOpen(true);
  };

  /* ---------------- chat history (tab section) ---------------- */
  const refreshHistory = () => {
    const uid = localStorage.getItem('userId');
    if (uid) listChats(uid).then(setChatHistory).catch(() => {});
  };

  // Start a brand-new chat (keeps existing saved chats in history).
  const startNewChat = () => {
    setChatId(`chat-${Date.now()}`);
    skipNextPersist.current = true;
    localStorage.removeItem(CHAT_STORAGE_KEY);
    setMessages(INITIAL_MESSAGES);
    setFlowStep(null);
    setHistoryOpen(false);
  };

  // Open a previously saved chat as the active conversation.
  const loadChat = (item) => {
    setChatId(item.chatId);
    setMessages(item.messages && item.messages.length ? item.messages : INITIAL_MESSAGES);
    setFlowStep(null);
    setHistoryOpen(false);
  };

  // Delete a saved chat; if it's the active one, start fresh.
  const removeChat = async (e, item) => {
    e.stopPropagation();
    const uid = localStorage.getItem('userId');
    await deleteChat(uid, item.chatId);
    setChatHistory((prev) => prev.filter((c) => c.chatId !== item.chatId));
    if (item.chatId === chatId) startNewChat();
  };

  /* ---------------- reset / back to start ---------------- */
  const resetAll = () => {
    clearTimeout(typingTimer.current);
    setFlowStep(null);
    setCourse(null);
    setSavedCourse(null);
    setAssessments([]);
    setAssessmentCourses([]);
    setSavedAssessments([]);
    setCurrentIdx(0);
    setQuestions([]);
    setQuestionDialogOpen(false);
    setQuestionCount(MAX_QUESTIONS);
    setSelectedTypes([]);
    setSelectedLevels([]);
    setShowUpgrade(false);
    setCourseName('');
    setGrade('');
    setAttachFile(null);
    setAttachText('');
    setBuildMode(null);
    setAutoCourse(null);
    setAutoAssessments([]);
    setAutoPreviewOpen(false);
    setInput('');
    setIsTyping(false);
    setLoadingLabel('');
    usageRef.current = undefined; // re-check the package on the next flow
    skipNextPersist.current = true;
    localStorage.removeItem(CHAT_STORAGE_KEY);
    setMessages(INITIAL_MESSAGES);
    /*
    setMessages([
      {
        from: 'bot',
        text: "Hi! I'm Yaki, the StudyAK assistant. 👋 Ask me anything, or pick a topic below.",
      },
    ]);
    */
  };

  /* ---------------- generic send ---------------- */
  const sendMessage = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setShowUpgrade(false); // any new action dismisses the upgrade CTA

    // route input into the active course flow if one is running
    if (flowStep === 'askName') return submitCourseName(trimmed);
    if (flowStep === 'askAttachment') {
      // Text is entered in the notes box on the attach card, so guide the user there.
      botReplyDelayed('Type your text in the notes box above, attach a PDF or image if you like, then tap Continue or Skip.', 300);
      return;
    }
    if (flowStep === 'askGrade') return submitGrade(trimmed);
    if (flowStep === 'askGradeLevel') return submitGradeLevel(trimmed);
    if (flowStep === 'askAssessmentCount') return submitAssessmentCount(trimmed);
    if (flowStep === 'askQuestionCount') return submitQuestionCount(trimmed);
    if (flowStep === 'askQuestionType') { botReplyDelayed('Please pick question type(s) below, then tap Continue.'); return; }
    if (flowStep === 'askAssessmentLevel') { botReplyDelayed('Please pick one assessment level below, then tap Continue.'); return; }
    if (flowStep === 'askSettings') { botReplyDelayed('Please tap Generate or Update settings below.'); return; }
    if (flowStep === 'chooseMode') { botReplyDelayed('Please pick ⚡ Fully automatic or ✍️ Step by step below.'); return; }
    if (flowStep === 'selectAssessmentCourse' && !/^\d+$/.test(trimmed) && !/create (an? )?assessment/i.test(trimmed)) {
      botReplyDelayed('Please pick one of your courses below (or type its number).');
      return;
    }
    if (flowStep === 'autoPreview') { botReplyDelayed('Please tap “Save all” or “Discard” below.'); return; }

    // detect a "create a course" intent from free text / quick reply
    if (/create (a )?course/i.test(trimmed)) return startCourseFlow();
    // "create assessment" starts the same flow against an existing course
    if (/create (an? )?assessment/i.test(trimmed)) return startAssessmentFlow();

    // Pick the course for a new assessment by typing its number.
    if (flowStep === 'selectAssessmentCourse') {
      const idx = parseInt(trimmed, 10) - 1;
      if (!isNaN(idx) && assessmentCourses[idx]) {
        setInput('');
        chooseAssessmentCourse(assessmentCourses[idx]);
      } else {
        botReplyDelayed('Please pick one of the courses below (or type its number).');
      }
      return;
    }

    // Pick from the duplicate-name list by typing a number.
    if (flowStep === 'selectCourse') {
      const idx = parseInt(trimmed, 10) - 1;
      if (!isNaN(idx) && courseCandidates[idx]) {
        pushUser(trimmed);
        setInput('');
        selectCourse(courseCandidates[idx]);
      } else {
        botReplyDelayed('Please pick one of the courses below (or type its number).');
      }
      return;
    }

    // "use course <name>" points the agent at a course by name (with a picker
    // when several courses share the name).
    const useMatch = trimmed.match(/^use course\s+(.+)/i);
    if (useMatch) {
      pushUser(trimmed);
      setInput('');
      resolveCourseByName(useMatch[1].trim());
      return;
    }

    // Greetings / FAQ get an instant local reply; everything else goes to the
    // Yaki agent (quiz, banks, adaptive, drafts, mistakes, domains, or guarded
    // chat) — Integration Plan v0.2.
    pushUser(trimmed);
    setInput('');
    const canned = getBotReply(trimmed);
    if (canned) {
      botReplyDelayed(canned, 500);
    } else {
      askAgent(trimmed);
    }
  };

  // Resolves a course by name. One match → use it; several → ask the user to
  // pick; none → tell them.
  const resolveCourseByName = async (name) => {
    setAgentBusy(true);
    setIsTyping(true);
    try {
      const matches = await findCoursesByName(name);
      if (matches.length === 0) {
        pushBot(`I couldn't find a course named "${name}". Check the name and try again.`);
      } else if (matches.length === 1) {
        setActiveCourseId(matches[0].courseId);
        pushBot(`Got it — I'll use "${matches[0].courseName}" for generation commands.`);
      } else {
        setCourseCandidates(matches);
        setFlowStep('selectCourse');
        pushBot(`I found ${matches.length} courses named "${name}". Which one? Pick below (or type its number).`);
      }
    } catch (err) {
      console.error(err);
      pushBot('Sorry — I couldn’t look up that course. Please try again.');
    } finally {
      setAgentBusy(false);
      setIsTyping(false);
    }
  };

  const selectCourse = (c) => {
    setActiveCourseId(c.courseId);
    setCourseCandidates([]);
    setFlowStep(null);
    pushBot(`Using "${c.courseName}". What would you like to do? (e.g. "quiz me on 10 questions", "plan a question bank")`);
  };

  // Routes a free-form message to the backend Yaki agent and renders the reply
  // plus a short summary of any generated questions / plan / drafts / signals.
  const askAgent = async (message) => {
    setAgentBusy(true);
    setIsTyping(true);
    try {
      const data = await askYakiAgent({
        message,
        courseId: activeCourseId,
        userId: localStorage.getItem('userId'),
      });
      let meta = '';
      if (Array.isArray(data.questions)) {
        meta = `✓ ${data.questions.length} ${data.mode || ''} question(s) generated`;
        if (data.testId) meta += ' · saved as draft';
      } else if (data.jobId) {
        meta = `Bank job ${data.jobId} started — say "review drafts" then "activate" once it finishes.`;
      } else if (data.plan) {
        meta = `Plan: ${data.plan.domainBanks?.length || 0} domain banks, ${data.plan.totalGeneratedItems} items.`;
      } else if (Array.isArray(data.domains)) {
        meta = `Domains: ${data.domains.join(', ')}`;
      } else if (Array.isArray(data.drafts)) {
        meta = data.drafts.length
          ? `Drafts: ${data.drafts.map((d) => `${d.name} (${d.questionCount})`).join('; ')}`
          : 'No drafts.';
      } else if (data.signals) {
        meta = `Weak areas: ${(data.signals.weakDomains || []).join(', ') || 'none yet'}`;
      }
      pushBot(data.response || 'Done.', { cached: !!data.cached });
      if (meta) pushBot(meta);
    } catch (err) {
      console.error(err);
      pushBot('Sorry — I had trouble with that. Please try again.');
    } finally {
      setAgentBusy(false);
      setIsTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const totalWeight = course
    ? course.domains.reduce((s, d) => s + (parseFloat(d.percentage) || 0), 0)
    : 0;

  const inputDisabled =
    !!loadingLabel ||
    agentBusy ||
    flowStep === 'generating' ||
    flowStep === 'submitting' ||
    flowStep === 'savingAssessments' ||
    flowStep === 'generatingQuestions' ||
    flowStep === 'savingQuestions' ||
    flowStep === 'autoGenerating' ||
    flowStep === 'autoSaving';

  const currentAssessment = savedAssessments[currentIdx];

  return (
    <div className="syk-chat" data-theme={portalTheme}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="syk-chat__panel"
            role="dialog"
            aria-label="Yaki AI Assistant chat"
            initial={{ opacity: 0, y: 40, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.92 }}
            transition={{ type: 'spring', damping: 20, stiffness: 220 }}
          >
            <div className="syk-chat__header">
              <div className="syk-chat__avatar">
                {logoOk ? (
                  <img src={LOGO_SRC} alt="Studyak" onError={() => setLogoOk(false)} />
                ) : (
                  <Sparkles size={22} strokeWidth={2.2} color="var(--syk-accent)" />
                )}
              </div>
              <div>
                <div className="syk-chat__title">Yaki AI Assistant</div>
                <div className="syk-chat__subtitle">
                  <span className="syk-chat__status-dot" />
                  Online — typically replies instantly
                </div>
              </div>
              <button
                className="syk-chat__close"
                onClick={() => setMemoryOpen((o) => !o)}
                aria-label="Memory files"
                title="Memory files"
              >
                <Database size={18} />
              </button>
              <button
                className="syk-chat__close"
                onClick={() => { setHistoryOpen((o) => !o); refreshHistory(); }}
                aria-label="Chat history"
                title="Chat history"
              >
                <History size={18} />
              </button>
              <button
                className="syk-chat__close"
                onClick={resetAll}
                aria-label="Restart from the beginning"
                title="Back to start"
              >
                <RotateCcw size={18} />
              </button>
              <button
                className="syk-chat__close"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>

            {/* Memory files (enterprise / course / learner) used as prompt context */}
            <MemoryPanel
              open={memoryOpen}
              onClose={() => setMemoryOpen(false)}
              userId={localStorage.getItem('userId')}
            />

            {/* Chat history (tab section) — saved per user; persists until deleted */}
            {historyOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 60,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: '#fff',
                  zIndex: 6,
                  overflowY: 'auto',
                  padding: 12,
                  borderBottomLeftRadius: 16,
                  borderBottomRightRadius: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <strong style={{ fontSize: 13 }}>Your chats</strong>
                  <button
                    className="syk-chat__chip"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    onClick={startNewChat}
                  >
                    <MessageSquarePlus size={14} /> New chat
                  </button>
                </div>
                {chatHistory.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: '#8a8a99', padding: '12px 4px' }}>
                    No saved chats yet. Your conversations will appear here.
                  </div>
                ) : (
                  chatHistory.map((item) => (
                    <div
                      key={item.chatId}
                      onClick={() => loadChat(item)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '8px 10px',
                        marginBottom: 6,
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: item.chatId === chatId ? '#ece9ff' : '#f4f5f7',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.title}
                        </div>
                        <div style={{ fontSize: 10.5, color: '#8a8a99' }}>
                          {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : ''}
                        </div>
                      </div>
                      <button
                        onClick={(e) => removeChat(e, item)}
                        aria-label="Delete chat"
                        title="Delete chat"
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#b4304a', padding: 4 }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            <div className="syk-chat__body" ref={bodyRef}>
              {messages.map((m, i) => (
                <div key={i} className={`syk-chat__row syk-chat__row--${m.from}`}>
                  <div className="syk-chat__bubble">
                    {m.text}
                    {m.cached && (
                      <span
                        title="Reused from memory — no new AI call"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 6,
                          fontSize: 10.5, color: '#5b54e6', fontWeight: 600,
                        }}
                      >
                        ⚡ instant (from memory)
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {/* build-mode picker (fully automatic vs step by step) */}
              {flowStep === 'chooseMode' && !isTyping && !loadingLabel && (
                <div className="syk-chat__grade-grid">
                  <button className="syk-chat__chip" style={{ fontWeight: 700 }} onClick={() => chooseMode('auto')}>
                    ⚡ Fully automatic
                  </button>
                  <button className="syk-chat__chip" onClick={() => chooseMode('semi')}>
                    ✍️ Step by step
                  </button>
                </div>
              )}

              {/* attach a PDF / image (optional) to base the course on */}
              {flowStep === 'askAttachment' && !isTyping && !loadingLabel && (
                <div className="syk-chat__grade-grid" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <label
                    className="syk-chat__chip"
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    📎 Attach PDF or image
                    <input
                      type="file"
                      hidden
                      accept=".pdf,image/*"
                      onChange={handleAttachSelect}
                    />
                  </label>

                  {attachFile && (
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 10px', borderRadius: 10,
                        background: 'var(--syk-surface)', border: '1px solid var(--syk-border)',
                        fontSize: 12, color: 'var(--syk-text)',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📄 {attachFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={removeAttachFile}
                        aria-label="Remove file"
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--syk-danger)', fontWeight: 700, padding: 2 }}
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <textarea
                    placeholder="Add text / instructions for the course (optional)…"
                    value={attachText}
                    onChange={(e) => setAttachText(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      minHeight: 58,
                      borderRadius: 12,
                      border: '1px solid var(--syk-border)',
                      background: 'var(--syk-surface-2)',
                      color: 'var(--syk-text)',
                      padding: '8px 10px',
                      fontSize: 12.5,
                      fontFamily: 'inherit',
                      lineHeight: 1.45,
                      outline: 'none',
                    }}
                  />

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="syk-chat__cta" onClick={continueFromAttachment}>
                      Continue →
                    </button>
                    <button className="syk-chat__chip" onClick={skipAttachment}>
                      Skip
                    </button>
                  </div>
                </div>
              )}

              {/* course picker for "Create Assessment" (the instructor's courses) */}
              {flowStep === 'selectAssessmentCourse' && !isTyping && !loadingLabel && assessmentCourses.length > 0 && (
                <div className="syk-chat__grade-grid">
                  {assessmentCourses.map((c, i) => (
                    <button
                      key={c.courseId || i}
                      className="syk-chat__chip"
                      title={`${(c.domains || []).length} domain(s)`}
                      onClick={() => chooseAssessmentCourse(c)}
                    >
                      {i + 1}. {c.courseName}
                      {c.code ? ` · ${c.code}` : ''}
                    </button>
                  ))}
                </div>
              )}

              {/* level quick-picks (K-12 / University / Professional Certification) */}
              {flowStep === 'askGrade' && !isTyping && !loadingLabel && (
                <div className="syk-chat__grade-grid">
                  {LEVEL_OPTIONS.map((g) => (
                    <button key={g} className="syk-chat__chip" onClick={() => submitGrade(g)}>
                      {g}
                    </button>
                  ))}
                </div>
              )}

              {/* K-12 grade picker (1–12) */}
              {flowStep === 'askGradeLevel' && !isTyping && !loadingLabel && (
                <div className="syk-chat__grade-grid">
                  {K12_GRADES.map((g) => (
                    <button key={g} className="syk-chat__chip" onClick={() => submitGradeLevel(g)}>
                      {g}
                    </button>
                  ))}
                </div>
              )}

              {/* duplicate-name course picker */}
              {flowStep === 'selectCourse' && !isTyping && !loadingLabel && courseCandidates.length > 0 && (
                <div className="syk-chat__grade-grid">
                  {courseCandidates.map((c, i) => (
                    <button
                      key={c.courseId || i}
                      className="syk-chat__chip"
                      title={c.courseId}
                      onClick={() => selectCourse(c)}
                    >
                      {i + 1}. {c.courseName}
                      {c.code ? ` · ${c.code}` : ''}
                      {c.courseId ? ` · #${String(c.courseId).slice(0, 6)}` : ''}
                    </button>
                  ))}
                </div>
              )}

              {/* generated course preview card */}
              {course && (flowStep === 'preview' || flowStep === 'submitting') && (
                <div className="syk-course-card">
                  <div className="syk-course-card__head">
                    <GraduationCap size={18} />
                    <span>{course.courseName}</span>
                  </div>
                  <div className="syk-course-card__meta">
                    <span className="syk-course-card__tag">{course.code}</span>
                    <span className="syk-course-card__tag">{course.courseType}</span>
                    <span className="syk-course-card__tag">{course.gradeLabel || course.level}</span>
                  </div>
                  {course.about && <p className="syk-course-card__about">{course.about}</p>}

                  <div className="syk-course-card__domains-title">
                    Domains <span>({totalWeight}% allocated)</span>
                  </div>
                  {course.domains.map((d, i) => (
                    <div key={i} className="syk-course-card__domain">
                      <div className="syk-course-card__domain-head">
                        <strong>{d.name}</strong>
                        <span className="syk-course-card__pct">{d.percentage}%</span>
                      </div>
                      {d.subDomain.length > 0 && (
                        <ul className="syk-course-card__subs">
                          {d.subDomain.map((s, k) => (
                            <li key={k}>
                              <b>{s.name}</b>
                              {s.subTopics ? ` — ${s.subTopics}` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  <div className="syk-course-card__actions">
                    <button
                      className="syk-course-card__btn syk-course-card__btn--primary"
                      onClick={handleAddCourse}
                      disabled={flowStep === 'submitting'}
                    >
                      <PlusCircle size={15} />
                      {flowStep === 'submitting' ? 'Adding…' : 'Add course'}
                    </button>
                    <button
                      className="syk-course-card__btn"
                      onClick={cancelCourse}
                      disabled={flowStep === 'submitting'}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}

              {/* fully-automatic full draft preview (course + assessments + questions) */}
              {flowStep === 'autoPreview' && autoCourse && (
                <div className="syk-course-card">
                  <div className="syk-course-card__head">
                    <GraduationCap size={18} />
                    <span>{autoCourse.courseName}</span>
                  </div>
                  <div className="syk-course-card__meta">
                    {autoCourse.code && <span className="syk-course-card__tag">{autoCourse.code}</span>}
                    {autoCourse.courseType && <span className="syk-course-card__tag">{autoCourse.courseType}</span>}
                    <span className="syk-course-card__tag">{autoCourse.gradeLabel || autoCourse.level || grade}</span>
                  </div>
                  {autoCourse.about && <p className="syk-course-card__about">{autoCourse.about}</p>}

                  <div className="syk-course-card__domains-title">
                    Assessments <span>({autoAssessments.length})</span>
                  </div>
                  {autoAssessments.map((a, i) => (
                    <div key={i} className="syk-course-card__domain">
                      <div className="syk-course-card__domain-head">
                        <strong>{a.name}</strong>
                        <span className="syk-course-card__pct">{a.questions.length} Q</span>
                      </div>
                      {a.questions.length > 0 && (
                        <ul className="syk-course-card__subs">
                          {a.questions.slice(0, 3).map((q, k) => (
                            <li key={k}>{q.question}</li>
                          ))}
                          {a.questions.length > 3 && <li>…and {a.questions.length - 3} more</li>}
                        </ul>
                      )}
                    </div>
                  ))}

                  <div className="syk-course-card__actions">
                    <button
                      className="syk-course-card__btn"
                      onClick={() => setAutoPreviewOpen(true)}
                      disabled={flowStep === 'autoSaving'}
                    >
                      <Eye size={15} />
                      Preview
                    </button>
                    <button
                      className="syk-course-card__btn syk-course-card__btn--primary"
                      onClick={handleAutoSaveAll}
                      disabled={flowStep === 'autoSaving'}
                    >
                      <Save size={15} />
                      {flowStep === 'autoSaving' ? 'Saving…' : 'Save all'}
                    </button>
                    <button
                      className="syk-course-card__btn"
                      onClick={handleAutoDiscard}
                      disabled={flowStep === 'autoSaving'}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}

              {/* number-of-assessments quick picks */}
              {flowStep === 'askAssessmentCount' && !isTyping && !loadingLabel && savedCourse && (
                <div className="syk-chat__grade-grid">
                  {Array.from(
                    { length: Math.min(4, maxAssessments(savedCourse)) },
                    (_, i) => i + 1
                  ).map((n) => (
                    <button
                      key={n}
                      className="syk-chat__chip"
                      onClick={() => submitAssessmentCount(String(n))}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    className="syk-chat__chip"
                    onClick={() => submitAssessmentCount('No thanks')}
                  >
                    No thanks
                  </button>
                </div>
              )}

              {/* generated assessment preview cards */}
              {assessments.length > 0 &&
                (flowStep === 'assessmentPreview' || flowStep === 'savingAssessments') && (
                  <div className="syk-assess-wrap">
                    {assessments.map((a, ai) => (
                      <div key={ai} className="syk-course-card">
                        <div className="syk-course-card__head">
                          <ClipboardList size={18} />
                          <span>{a.name}</span>
                        </div>
                        <div className="syk-course-card__meta">
                          <span className="syk-course-card__tag">{a.examCode}</span>
                          <span className="syk-course-card__tag">{a.examTime} min</span>
                          <span className="syk-course-card__tag">{a.totalMarks} marks</span>
                        </div>
                        {a.domains.map((d, di) => (
                          <div key={di} className="syk-course-card__domain">
                            <div className="syk-course-card__domain-head">
                              <strong>{d.name}</strong>
                              {d.percentage ? (
                                <span className="syk-course-card__pct">{d.percentage}%</span>
                              ) : null}
                            </div>
                            {d.subDomain.length > 0 && (
                              <ul className="syk-course-card__subs">
                                {d.subDomain.map((s, si) => (
                                  <li key={si}>
                                    <b>{s.name}</b>
                                    {s.subTopics ? ` — ${s.subTopics}` : ''}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}

                        {/* Per-assessment domain picker — choose which domains this
                            assessment covers (overrides the automatic even split). */}
                        {savedCourse && (savedCourse.domains || []).length > 0 && (
                          <div className="syk-assess-picker">
                            <span className="syk-assess-picker__label">Choose domains</span>
                            <div className="syk-assess-picker__chips">
                              {savedCourse.domains.map((d) => {
                                const on = (a.domains || []).some(
                                  (x) => domainKey(x) === domainKey(d)
                                );
                                return (
                                  <button
                                    key={domainKey(d)}
                                    type="button"
                                    className={`syk-assess-chip${on ? ' syk-assess-chip--on' : ''}`}
                                    onClick={() => toggleAssessmentDomain(ai, d)}
                                    disabled={flowStep === 'savingAssessments'}
                                  >
                                    {on ? '✓ ' : ''}{d.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Per-assessment question-type picker — used when this
                            assessment's questions are generated. */}
                        <div className="syk-assess-picker">
                          <span className="syk-assess-picker__label">Question type</span>
                          <div className="syk-assess-picker__chips">
                            {QUESTION_TYPES.map((t) => {
                              const on = (a.questionTypes || []).includes(t);
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  className={`syk-assess-chip${on ? ' syk-assess-chip--on' : ''}`}
                                  onClick={() => toggleAssessmentType(ai, t)}
                                  disabled={flowStep === 'savingAssessments'}
                                >
                                  {on ? '✓ ' : ''}{t}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="syk-course-card__actions">
                      <button
                        className="syk-course-card__btn syk-course-card__btn--primary"
                        onClick={handleSaveAssessments}
                        disabled={flowStep === 'savingAssessments'}
                      >
                        <PlusCircle size={15} />
                        {flowStep === 'savingAssessments' ? 'Saving…' : 'Agree & save'}
                      </button>
                      <button
                        className="syk-course-card__btn"
                        onClick={cancelAssessments}
                        disabled={flowStep === 'savingAssessments'}
                      >
                        Discard
                      </button>
                    </div>
                  </div>
                )}

              {/* number-of-questions quick picks (or type any number up to MAX_QUESTIONS) */}
              {flowStep === 'askQuestionCount' && !isTyping && !loadingLabel && currentAssessment && (
                <div className="syk-chat__grade-grid">
                  {QUESTION_OPTIONS.map((n) => (
                    <button
                      key={n}
                      className="syk-chat__chip"
                      onClick={() => submitQuestionCount(String(n))}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    className="syk-chat__chip"
                    onClick={() => submitQuestionCount('Skip')}
                  >
                    Skip
                  </button>
                </div>
              )}

              {/* question TYPE multi-select */}
              {flowStep === 'askQuestionType' && !isTyping && !loadingLabel && currentAssessment && (
                <div className="syk-chat__grade-grid">
                  {QUESTION_TYPES.map((t) => {
                    const on = selectedTypes.includes(t);
                    return (
                      <button
                        key={t}
                        className="syk-chat__chip"
                        onClick={() => toggleType(t)}
                        style={on ? { background: 'var(--syk-accent)', color: '#fff', borderColor: 'var(--syk-accent)' } : undefined}
                      >
                        {on ? '✓ ' : ''}{t}
                      </button>
                    );
                  })}
                  <button className="syk-chat__cta" onClick={continueTypes}>
                    Continue →
                  </button>
                </div>
              )}

              {/* Bloom's assessment-level multi-select */}
              {flowStep === 'askAssessmentLevel' && !isTyping && !loadingLabel && currentAssessment && (
                <div className="syk-chat__grade-grid">
                  {assessmentLevels.map((lvl) => {
                    const on = selectedLevels.includes(lvl.label);
                    return (
                      <button
                        key={lvl.label}
                        className="syk-chat__chip"
                        title={lvl.tooltip}
                        onClick={() => toggleLevel(lvl.label)}
                        style={on ? { background: 'var(--syk-accent)', color: '#fff', borderColor: 'var(--syk-accent)' } : undefined}
                      >
                        {on ? '✓ ' : ''}{lvl.label}
                      </button>
                    );
                  })}
                  <button className="syk-chat__cta" onClick={continueLevels}>
                    Continue →
                  </button>
                </div>
              )}

              {/* confirm: generate now or update settings */}
              {flowStep === 'askSettings' && !isTyping && !loadingLabel && currentAssessment && (
                <div className="syk-chat__grade-grid">
                  <button
                    className="syk-chat__chip"
                    onClick={() => runGenerateQuestions(questionCount, selectedTypes, selectedLevels)}
                    style={{ background: 'var(--syk-accent)', color: '#fff', borderColor: 'var(--syk-accent)', fontWeight: 700 }}
                  >
                    Generate
                  </button>
                  <button className="syk-chat__chip" onClick={updateSettings}>
                    Update settings
                  </button>
                </div>
              )}

              {/* generated question actions */}
              {questions.length > 0 &&
                (flowStep === 'questionPreview' || flowStep === 'savingQuestions') && (
                  <div className="syk-question-actions">
                    {currentAssessment && (
                      <div className="syk-q-title">
                        <ClipboardList size={15} />
                        {currentAssessment.name} · {questions.length} questions
                      </div>
                    )}
                    <div className="syk-course-card__actions">
                      <button
                        className="syk-course-card__btn syk-course-card__btn--primary"
                        onClick={handleShowQuestions}
                        disabled={flowStep === 'savingQuestions'}
                      >
                        <Eye size={15} />
                        Preview Questions
                      </button>
                      <button
                        className="syk-course-card__btn"
                        onClick={() => handleSaveQuestions()}
                        disabled={flowStep === 'savingQuestions'}
                      >
                        <Save size={15} />
                        {flowStep === 'savingQuestions' ? 'Saving...' : 'Save questions'}
                      </button>
                    </div>
                  </div>
                )}

              {isTyping && (
                <div className="syk-chat__row syk-chat__row--bot">
                  <div className="syk-chat__bubble">
                    <span className="syk-chat__typing">
                      <span />
                      <span />
                      <span />
                    </span>
                  </div>
                </div>
              )}

              {/* inline labelled loading indicator for any awaiting step */}
              {loadingLabel && (
                <div className="syk-chat__row syk-chat__row--bot">
                  <div className="syk-chat__loading" style={progress ? { flexDirection: 'column', alignItems: 'stretch', gap: 8 } : undefined}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="syk-chat__spinner" />
                      <span>{loadingLabel}</span>
                    </div>
                    {/* Progress for the long automatic build, so it never looks stuck. */}
                    {progress && progress.total > 0 && (
                      <div>
                        <div
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={progress.total}
                          aria-valuenow={progress.done}
                          aria-label="Course generation progress"
                          style={{
                            height: 6,
                            borderRadius: 999,
                            background: 'rgba(0,0,0,.1)',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.round((progress.done / progress.total) * 100)}%`,
                              height: '100%',
                              background: 'var(--syk-accent)',
                              transition: 'width .3s ease',
                            }}
                          />
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                          {progress.done} of {progress.total} assessment{progress.total > 1 ? 's' : ''} ready
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* upgrade-plan CTA shown after a package limit is hit */}
              {showUpgrade && !loadingLabel && !isTyping && (
                <div className="syk-chat__grade-grid">
                  <button
                    className="syk-chat__chip"
                    onClick={() => { setShowUpgrade(false); setIsOpen(false); navigate('/Portal/Instructor/billing'); }}
                    style={{ background: 'var(--syk-accent)', color: '#fff', borderColor: 'var(--syk-accent)', fontWeight: 700 }}
                  >
                    ⬆ Upgrade plan
                  </button>
                </div>
              )}
            </div>

            {/* quick replies show until the user starts a conversation */}
            {messages.length <= 1 && !flowStep && (
              <div className="syk-chat__quick">
                {QUICK_REPLIES.map((q) => {
                  if (q.includes('Create a course')) {
                    return (
                      <button key={q} className="syk-chat__chip" onClick={startCourseFlow}>
                        {q}
                      </button>
                    );
                  }
                  if (q.includes('Create Assessment')) {
                    return (
                      <button key={q} className="syk-chat__chip" onClick={startAssessmentFlow}>
                        {q}
                      </button>
                    );
                  }
                  return (
                    <button key={q} className="syk-chat__chip" onClick={() => sendMessage(q)}>
                      {q}
                    </button>
                  );
                })}
              </div>
            )}

            <form className="syk-chat__input-bar" onSubmit={handleSubmit}>
              <input
                className="syk-chat__input"
                placeholder={
                  flowStep === 'askName'
                    ? 'Enter the course name…'
                    : flowStep === 'askAttachment'
                    ? 'Use the notes box above…'
                    : flowStep === 'askGrade'
                    ? 'Pick a level above…'
                    : flowStep === 'askGradeLevel'
                    ? 'Pick a grade (1–12) above…'
                    : flowStep === 'askAssessmentCount'
                    ? 'How many assessments?'
                    : flowStep === 'askQuestionCount'
                    ? `Type a number up to ${MAX_QUESTIONS} or pick one above…`
                    : flowStep === 'askQuestionType'
                    ? 'Pick question type(s) above…'
                    : flowStep === 'askAssessmentLevel'
                    ? 'Pick assessment level(s) above…'
                    : flowStep === 'askSettings'
                    ? 'Generate or update settings above…'
                    : 'Type your message…'
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={inputDisabled}
                aria-label="Message"
              />
              <button
                type="submit"
                className="syk-chat__send"
                disabled={!input.trim() || inputDisabled}
                aria-label="Send message"
              >
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="syk-chat__launcher">
        {!isOpen && (
          <span className="syk-chat__launcher-label" aria-hidden="true">
            Ask Yaki 👋
          </span>
        )}
        <motion.button
          className="syk-chat__toggle"
          onClick={() => setIsOpen((o) => !o)}
          whileTap={{ scale: 0.9 }}
          aria-label={isOpen ? 'Close chat' : 'Ask Yaki, the StudyAK assistant'}
          aria-expanded={isOpen}
        >
          {isOpen ? (
            <X size={26} />
          ) : logoOk ? (
            <img
              className="syk-chat__toggle-logo"
              src={LOGO_SRC}
              alt=""
              onError={() => setLogoOk(false)}
            />
          ) : (
            <Sparkles size={28} strokeWidth={2.2} />
          )}
          {!isOpen && <span className="syk-chat__badge" />}
        </motion.button>
      </div>

      {/* Full question review dialog — opens (with the chat minimized) when the
          user taps "Show questions". Wrapped in the Instructor design system so
          it looks right outside the Instructor route tree. */}
      <MuiThemeProvider theme={muiTheme}>
        <ThemeProvider theme={scTheme}>
          <Questionsdialog
            open={questionDialogOpen}
            handleClose={handleDialogClose}
            questions={questions}
            onSave={handleDialogSave}
            testId={currentAssessment ? currentAssessment.testId : ''}
            // The shared passage generated for this batch, so Passage questions show
            // the reading they refer to instead of an empty preview.
            passage={questions.find((q) => q.questionType === 'Passage' && q.passage)?.passage || ''}
            caseStudyOption=""
            mediaUrl=""
            questionType={selectedTypes.join(', ')}
            assessmentLevel={selectedLevels}
            directAttachmentType={null}
            readOnly
          />
          <AutoPreviewDialog
            open={autoPreviewOpen}
            onClose={() => setAutoPreviewOpen(false)}
            course={autoCourse}
            assessments={autoAssessments}
          />
        </ThemeProvider>
      </MuiThemeProvider>
    </div>
  );
};

export default ChatWidget;
